import { ErrorLink } from '@apollo/client/link/error';
import { GraphQLError } from 'graphql';
import { hasValue } from '@famly/stat_ts-utils_has-value';
import { Headers } from './constants';
import { isErrorNamed } from './is-error-named';
import { readErrorHandlerOptions } from './read-error-handler-options';
import { readRestError } from './read-rest-error';
import { readServerErrorObject } from './read-server-error-object';
/**
 * Type guard to check if an error is a ServerError.
 *
 * @param error - The network error to check
 * @returns True if the error is a ServerError
 */
export const isServerError = (error) => isErrorNamed(error, 'ServerError');
/**
 * Type guard to check if an error is a ServerParseError.
 *
 * @param error - The network error to check
 * @returns True if the error is a ServerParseError
 */
export const isServerParseError = (error) => isErrorNamed(error, 'ServerParseError');
/**
 * Creates an error link for handling GraphQL and network errors.
 * Deduplicates GraphQL errors and handles REST errors from the core PHP project.
 *
 * @param onErrorCallback - Callback function that receives error information
 * @returns ErrorLink instance configured for error handling
 */
export const createErrorLink = (onErrorCallback) => new ErrorLink(options => {
    const { operation } = options;
    const { graphQLErrors, networkError, response } = readErrorHandlerOptions(options);
    const context = operation.getContext();
    const rawResponse = context.response;
    const requestUUID = rawResponse && rawResponse.headers.get(Headers.XFamlyRequestUuid);
    const name = operation.operationName;
    const responseErrors = response && response.errors ? response.errors : null;
    const extra = {
        name,
        requestUUID,
        responseErrors,
    };
    if (graphQLErrors) {
        // No reason to log the same error multiple times per request
        const deDuplicatedErrors = Array.from(new Map(graphQLErrors.map(error => {
            const key = error.message + (error.extensions ? error.extensions.errorCode : '');
            return [key, error];
        })).values());
        deDuplicatedErrors.map(error => {
            onErrorCallback(error, {
                ...extra,
                log: hasValue(error.extensions) && hasValue(error.extensions.log)
                    ? error.extensions.log
                    : true,
            }, null, context, 'graphql-error');
        });
    }
    const restError = (context === null || context === void 0 ? void 0 : context.clientName) === 'restGraphQl' ? readRestError(networkError) : null;
    if (restError) {
        // Apollo doesn't have any way of handling errors returned by `core` when using RestLink.
        // If we don't do anything, we'll end up showing the user a generic error message like:
        // "Response not successful: Received status code 400"
        // To avoid this, we "cheat" and construct a new error, and pass it along as if it were
        // a regular GraphQL error.
        const graphQLError = new GraphQLError(restError.error, {
            extensions: {
                errorCode: restError.errorCode,
                log: restError.log,
                display: 'Transient',
                severity: 'Error',
            },
        });
        onErrorCallback(graphQLError, {
            ...extra,
            log: restError.log,
        }, null, context, 'graphql-error');
    }
    else if (networkError) {
        const serverErrorResult = isServerError(networkError) ? readServerErrorObject(networkError) : null;
        const networkExtra = serverErrorResult && serverErrorResult.errorCode
            ? {
                ...serverErrorResult,
                ...extra,
                log: hasValue(serverErrorResult.log) ? serverErrorResult.log : true,
            }
            : null;
        const hasOnlyNetworkError = !hasValue(graphQLErrors) || graphQLErrors.length === 0;
        // Only report network errors to Sentry - don't show them in the UI
        onErrorCallback(hasOnlyNetworkError ? networkError : undefined, networkExtra, hasOnlyNetworkError && serverErrorResult && hasValue(serverErrorResult.message)
            ? serverErrorResult.message
            : null, context, 'network-error');
    }
});
